/**
 * Fixed RFC-4122 namespace for Voll customer publicIds. NEVER change this
 * value — changing it re-keys every publicId and breaks every existing link
 * back to the legacy source id (D-PID-02). Locked at plan time.
 */
export const NAMESPACE_VOLL_CUSTOMER = '9214e803-6022-4411-8def-834aaa71285f';

/**
 * Pure, dependency-free SHA-1 (RFC 3174) over a byte array → 20-byte digest.
 *
 * Hand-rolled rather than `node:crypto` because this module lives in
 * `@voll-intelligence/domain`, which is bundled for the BROWSER (web / web-chat
 * via Vite). A `node:crypto` import gets externalized to `__vite-browser-external`
 * and `createHash` resolves to `undefined`, breaking the browser build. A pure
 * implementation keeps the package isomorphic (domain purity: no platform
 * imports) while producing byte-identical output to `crypto.createHash('sha1')`
 * — the locked golden value in `public_id.test.ts` guards that equivalence.
 */
function sha1(bytes: Uint8Array): Uint8Array {
  const ml = bytes.length * 8;
  // Pad: append 0x80, zero-fill to 56 mod 64, then the 64-bit big-endian bit length.
  const totalLen = (bytes.length + 1 + 8 + 63) & ~63;
  const msg = new Uint8Array(totalLen);
  msg.set(bytes);
  msg[bytes.length] = 0x80;
  const view = new DataView(msg.buffer);
  view.setUint32(totalLen - 8, Math.floor(ml / 0x1_0000_0000));
  view.setUint32(totalLen - 4, ml >>> 0);

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;
  const rotl = (n: number, s: number): number => ((n << s) | (n >>> (32 - s))) >>> 0;
  const w = new Uint32Array(80);

  for (let i = 0; i < totalLen; i += 64) {
    for (let j = 0; j < 16; j++) w[j] = view.getUint32(i + j * 4);
    for (let j = 16; j < 80; j++) {
      w[j] = rotl((w[j - 3] ?? 0) ^ (w[j - 8] ?? 0) ^ (w[j - 14] ?? 0) ^ (w[j - 16] ?? 0), 1);
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    for (let j = 0; j < 80; j++) {
      let f: number;
      let k: number;
      if (j < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (j < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (j < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const t = (rotl(a, 5) + f + e + k + (w[j] ?? 0)) >>> 0;
      e = d;
      d = c;
      c = rotl(b, 30);
      b = a;
      a = t;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  const out = new Uint8Array(20);
  const outView = new DataView(out.buffer);
  outView.setUint32(0, h0);
  outView.setUint32(4, h1);
  outView.setUint32(8, h2);
  outView.setUint32(12, h3);
  outView.setUint32(16, h4);
  return out;
}

/**
 * RFC-4122 §4.3 name-based UUIDv5 (SHA-1) over the legacy numeric source id,
 * scoped to {@link NAMESPACE_VOLL_CUSTOMER}. Pure + deterministic + isomorphic:
 * the same input always yields the same UUID (idempotent across re-backfill /
 * CDC re-upsert, no random, no insert race), distinct inputs yield distinct
 * UUIDs.
 *
 * This is the sole mint site for the opaque, non-enumerable customer identity
 * (D-PID-02). Built on a pure SHA-1 — no `node:crypto`, no `uuid` dependency —
 * so it works unchanged in both Node services and the browser bundles.
 */
export function computeCustomerPublicId(legacySourceId: string): string {
  const namespacePairs = NAMESPACE_VOLL_CUSTOMER.replaceAll('-', '').match(/.{2}/g);
  if (!namespacePairs) throw new Error('Invalid VOLL customer UUID namespace');
  const nsBytes = namespacePairs.map((h) => Number.parseInt(h, 16));
  const nameBytes = new TextEncoder().encode(legacySourceId);
  const input = new Uint8Array(nsBytes.length + nameBytes.length);
  input.set(nsBytes, 0);
  input.set(nameBytes, nsBytes.length);

  const b = sha1(input).subarray(0, 16);
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x50; // version 5
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80; // RFC variant
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20)}`;
}

/**
 * Null-safe wire-output scrub: maps a legacy numeric source id to its opaque
 * publicId, passing `null` through unchanged. The SAME deterministic
 * {@link computeCustomerPublicId} the mapper uses — single source, no duplicate
 * mint site. Applied as an OUTPUT transform just before each view `.parse()`
 * (VIEW-01 / D-PID-03); the query INPUT keeps the source id (Plan 02).
 */
export function sourceIdToPublicId(sourceId: string | null): string | null {
  return sourceId == null ? null : computeCustomerPublicId(sourceId);
}

/**
 * The single reusable voucher identity scrub (T-01-09 / T-01-14). Opaquifies
 * EVERY source-id-bearing person on the voucher — `traveler.id` (who travels),
 * `requester.id` (who booked it) and each `companions[].id` (who else travels).
 * The latter two may NOT be mirrored Customers; their publicId is then a one-way
 * opaque token, still correct for anti-enumeration. Every other field is left
 * untouched. Applied at EVERY voucher emit site, including vouchers nested under
 * trips / expense days / dossier inactiveVouchers, before the enclosing `.parse()`.
 *
 * A person added to the voucher MUST be added here too — an un-scrubbed id is a
 * raw enumerable source id on the wire.
 *
 * Generic over the pre-parse voucher shape so it works for both the service-local
 * branded `Voucher` and the brand-free `VoucherView` (the derive-on-read path).
 */
export function scrubVoucher<
  T extends {
    traveler?: { id: string } | null;
    requester?: { id: string } | null;
    companions?: { id: string }[];
  },
>(v: T): T {
  return {
    ...v,
    traveler: v.traveler ? { ...v.traveler, id: sourceIdToPublicId(v.traveler.id) } : v.traveler,
    requester: v.requester
      ? { ...v.requester, id: sourceIdToPublicId(v.requester.id) }
      : v.requester,
    companions: v.companions?.map((c) => ({ ...c, id: sourceIdToPublicId(c.id) })),
  };
}
